<template>
  <div class="receipt">
    <!-- Solo al imprimir: en pantalla el comercio ya está identificado por el
         encabezado de la página o del modal, pero el papel viaja solo. -->
    <div class="receipt-brand">
      <img
        :src="business.logo"
        :alt="business.name"
        class="receipt-brand-logo"
      >
      <div class="receipt-brand-title">
        Comprobante de pago
      </div>
    </div>

    <div class="receipt-amount">
      {{ receipt.currency }} {{ formatAmount(receipt.amount) }}
    </div>

    <dl class="receipt-rows">
      <div>
        <dt>Referencia</dt>
        <dd>{{ receipt.reference }}</dd>
      </div>
      <div v-if="receipt.receipt_number">
        <dt>Recibo</dt>
        <dd>{{ receipt.receipt_number }}</dd>
      </div>
      <div>
        <dt>Contrato</dt>
        <dd>{{ receipt.contract_code }}</dd>
      </div>
      <div v-if="receipt.holder_name">
        <dt>Titular</dt>
        <dd>{{ receipt.holder_name }}</dd>
      </div>
      <div>
        <dt>Autorización</dt>
        <dd>{{ receipt.authorization }}</dd>
      </div>
      <div v-if="receipt.order_id">
        <dt>Orden</dt>
        <dd>{{ receipt.order_id }}</dd>
      </div>
      <div v-if="receipt.card">
        <dt>Tarjeta</dt>
        <dd>{{ receipt.card }}</dd>
      </div>
      <div>
        <dt>Fecha</dt>
        <dd>{{ receipt.paid_at }}</dd>
      </div>
    </dl>

    <div class="receipt-brand receipt-footer">
      {{ business.legalName }} · {{ business.address }}<br>
      {{ business.phone }} · {{ business.email }}
    </div>

    <div
      v-if="printable"
      class="text-center"
    >
      <b-button
        variant="outline-primary"
        size="sm"
        class="no-print mt-1"
        @click="print"
      >
        <feather-icon
          icon="PrinterIcon"
          size="14"
          class="mr-50"
        />Imprimir comprobante
      </b-button>
    </div>
  </div>
</template>

<script>
import { BButton } from "bootstrap-vue"
import business from "@/business"

/**
 * Comprobante de un pago aprobado.
 *
 * Vive en un solo componente porque se muestra en dos lugares: al terminar de
 * pagar y en el historial del perfil. Los campos y sus rótulos son los que AZUL
 * certificó, así que tener una sola copia evita que las dos pantallas se
 * separen y una quede fuera de lo aprobado.
 */
export default {
  components: { BButton },

  props: {
    receipt: {
      type: Object,
      required: true,
    },
    printable: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return { business }
  },

  methods: {
    formatAmount(value) {
      return new Intl.NumberFormat("es-DO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
    },
    print() {
      window.print()
    },
  },
}
</script>

<style lang="scss" scoped>
.receipt {
    padding: 1rem;
    text-align: left;
    background-color: #f8f8f8;
    border-radius: 0.5rem;
}

.receipt-amount {
    margin-bottom: 0.75rem;
    font-size: 1.5rem;
    font-weight: 600;
    text-align: center;
}

.receipt-rows > div {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.3rem 0;
    font-size: 0.8125rem;
    border-bottom: 1px solid rgba(34, 41, 47, 0.08);
}

.receipt-rows > div:last-child {
    border-bottom: 0;
}

.receipt-rows dt {
    font-weight: 400;
    color: #6e6b7b;
}

.receipt-rows dd {
    margin: 0;
    font-weight: 600;
    text-align: right;
    word-break: break-all;
}
</style>

<style>
/* En pantalla el encabezado sobra: la página ya identifica al comercio. Vive
   fuera del bloque con ámbito para que la regla de impresión pueda ganarle —
   un selector con ámbito lleva un atributo extra y tiene más especificidad. */
.receipt-brand {
    display: none;
}

/* Impresión del comprobante.
   Sin ámbito porque hay que ocultar todo lo que vive fuera de este componente
   — el layout del portal en un caso, el modal del perfil en el otro. Se apunta
   a .receipt y no al contenedor de cada pantalla: ese selector existía solo en
   la confirmación y desde el perfil salía en blanco. */
@media print {
    /* Hoja tipo comprobante en vez de A4.
       El uso real es guardarlo como PDF o mostrarlo desde el teléfono, no
       imprimirlo en papel de oficina: en A4 el contenido ocupaba media hoja y
       el resto quedaba en blanco. */
    @page {
        size: 105mm 148mm;
        margin: 8mm;
    }

    body * {
        visibility: hidden;
    }

    .receipt,
    .receipt * {
        visibility: visible;
    }

    /* fixed y no absolute: absolute se posiciona contra el ancestro
       posicionado más cercano — el modal, en el perfil — y el comprobante
       terminaba flotando a media hoja en vez de arriba. */
    .receipt {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        /* Angosto y centrado: a ancho completo la etiqueta queda a un extremo
           de la hoja y el valor al otro, y deja de leerse como un comprobante. */
        width: 100% !important;
        max-width: 100% !important;
        margin: 0 auto !important;
        padding: 0 !important;
        background-color: transparent !important;
    }

    .receipt-brand {
        display: block;
        margin-bottom: 0.6rem;
        text-align: center;
    }

    .receipt-brand-logo {
        height: 34px;
        margin-bottom: 0.35rem;
    }

    .receipt-brand-title {
        font-size: 8.5pt;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
    }

    /* En papel el cuerpo necesita más presencia que en una tarjeta de pantalla. */
    .receipt-amount {
        font-size: 15pt !important;
        margin-bottom: 0.6rem !important;
    }

    .receipt-rows > div {
        padding: 0.25rem 0 !important;
        font-size: 8pt !important;
    }

    .receipt-footer {
        margin-top: 0.75rem;
        padding-top: 0.5rem;
        border-top: 1px solid rgba(34, 41, 47, 0.15);
        font-size: 6.5pt;
        line-height: 1.45;
        color: #555;
    }

    .no-print {
        display: none !important;
    }

    /* Sin esto el alto del modal arrastra una segunda hoja en blanco. */
    html,
    body {
        height: auto !important;
        overflow: visible !important;
    }
}
</style>
